import React, { useState, useEffect } from 'react';
import { userApi, User, UserCreate } from '../api/users';
import { organizationApi, Organization } from '../api/organizations';
import { storeApi, Store } from '../api/stores';
import { pageApi, PageWithComponents } from '../api/pages';

interface UsersProps {
  userInfo?: any;
}

const Users: React.FC<UsersProps> = ({ userInfo }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [dashboardPages, setDashboardPages] = useState<PageWithComponents[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStorePermissionsModal, setShowStorePermissionsModal] = useState(false);
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserCreate>({
    cognito_username: '',
    user_email: '',
    org_id: userInfo?.org_id || '',
    is_active: true,
    assigned_by: 'system'
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [createError, setCreateError] = useState<string | null>(null);

  // Dropdown data never paginates, so it only needs loading once.
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Wait for a pause in typing so we don't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // The server does the filtering and slicing, so refetch on any of these.
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch]);

  // Update formData when userInfo changes
  useEffect(() => {
    if (userInfo?.org_id && !userInfo?.is_admin) {
      setFormData(prev => ({
        ...prev,
        org_id: userInfo.org_id
      }));
    }
  }, [userInfo]);

  const loadReferenceData = async () => {
    try {
      const [orgsData, storesData, pagesData] = await Promise.all([
        organizationApi.list(),
        storeApi.list(0, 1000),
        pageApi.getPagesWithComponents()
      ]);
      setOrganizations(orgsData);
      setStores(storesData);
      setDashboardPages(pagesData);
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  // Deliberately does not set `loading` back to true: that would unmount the
  // table and the search box mid-typing and steal focus. `loading` only covers
  // the very first render.
  const loadUsers = async () => {
    try {
      const response = await userApi.list((page - 1) * pageSize, pageSize, debouncedSearch);
      setUsers(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Creating user with formData:', formData);
      console.log('Current userInfo:', userInfo);
      setCreateError(null);
      await userApi.create(formData);
      setShowCreateModal(false);
      // Reset form but preserve org_id for non-admin users
      const resetOrgId = userInfo?.is_admin ? '' : userInfo?.org_id || '';
      setFormData({ cognito_username: '', user_email: '', org_id: resetOrgId, is_active: true, assigned_by: 'system' });
      loadUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      const detail = error.response?.data?.detail;
      // FastAPI sends a string for HTTPException but a list of objects for
      // validation errors; rendering the raw object would crash React.
      setCreateError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d: any) => d.msg).join(', ')
            : 'Failed to create user. Please try again.'
      );
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await userApi.update(selectedUser.id, formData);
      setShowEditModal(false);
      // Reset form but preserve org_id for non-admin users
      const resetOrgId = userInfo?.is_admin ? '' : userInfo?.org_id || '';
      setFormData({ cognito_username: '', user_email: '', org_id: resetOrgId, is_active: true, assigned_by: 'system' });
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDisable = async (id: number) => {
    if (window.confirm('Are you sure you want to disable this user?')) {
      try {
        await userApi.disable(id);
        loadUsers();
      } catch (error) {
        console.error('Error disabling user:', error);
      }
    }
  };

  const openCreateModal = () => {
    // Ensure org_id is set correctly when opening the modal
    const initialOrgId = userInfo?.is_admin ? '' : userInfo?.org_id || '';
    setFormData({
      cognito_username: '',
      user_email: '',
      org_id: initialOrgId,
      is_active: true,
      assigned_by: 'system'
    });
    setCreateError(null);
    setShowCreateModal(true);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      cognito_username: user.cognito_username,
      user_email: user.user_email,
      org_id: user.org_id,
      is_active: user.is_active,
      assigned_by: user.assigned_by
    });
    setShowEditModal(true);
  };

  const openStorePermissionsModal = (user: User) => {
    setSelectedUser(user);
    setShowStorePermissionsModal(true);
  };

  const openUserPermissionsModal = (user: User) => {
    setSelectedUser(user);
    setShowUserPermissionsModal(true);
  };

  const handleStorePermissionsUpdate = async (selectedStores: string[]) => {
    if (!selectedUser) return;
    
    try {
      // First, get current store access
      const currentStoreAccess = await userApi.getStoreAccess(selectedUser.id);
      const currentStoreIds = currentStoreAccess.map((access: any) => access.store_id);
      
      // Find stores to add (selected but not currently have access)
      const storesToAdd = selectedStores.filter(storeId => !currentStoreIds.includes(storeId));
      
      // Find stores to remove (currently have access but not selected)
      const storesToRemove = currentStoreIds.filter(storeId => !selectedStores.includes(storeId));
      
      // Add new store access
      for (const storeId of storesToAdd) {
        await userApi.addStoreAccess(selectedUser.id, {
          user_email: selectedUser.user_email,
          org_id: selectedUser.org_id,
          store_id: storeId,
          access_level: 'admin',
          granted_by: 'admin'
        });
      }
      
      // Remove store access for unchecked stores
      for (const storeId of storesToRemove) {
        await userApi.removeStoreAccess(selectedUser.id, storeId);
      }
      
      setShowStorePermissionsModal(false);
      // Reload data to show updated permissions
      loadUsers();
    } catch (error) {
      console.error('Error updating store permissions:', error);
    }
  };

  const handleUserPermissionsUpdate = async (selectedStores: string[], selectedPages: string[], selectedComponents: string[]) => {
    if (!selectedUser) return;
    
    try {
      // First, get current permissions
      const currentStoreAccess = await userApi.getStoreAccess(selectedUser.id);
      const currentDashboardAccess = await userApi.getDashboardAccess(selectedUser.id);

      // Handle store access - add new and remove unchecked
      const currentStoreIds = currentStoreAccess.map((access: any) => access.store_id);
      const storesToAdd = selectedStores.filter(storeId => !currentStoreIds.includes(storeId));
      const storesToRemove = currentStoreIds.filter(storeId => !selectedStores.includes(storeId));

      // Add new store access
      for (const storeId of storesToAdd) {
        await userApi.addStoreAccess(selectedUser.id, {
          user_email: selectedUser.user_email,
          org_id: selectedUser.org_id,
          store_id: storeId,
          access_level: 'admin',
          granted_by: 'admin'
        });
      }

      // Remove unchecked store access
      for (const storeId of storesToRemove) {
        await userApi.removeStoreAccess(selectedUser.id, storeId);
      }

      // Add dashboard page access (only if not already exists)
      for (const pageId of selectedPages) {
        const exists = currentDashboardAccess.some((access: any) => 
          access.page_id === pageId && access.component_id === '*'
        );
        if (!exists) {
          await userApi.addDashboardAccess(selectedUser.id, {
            user_email: selectedUser.user_email,
            org_id: selectedUser.org_id,
            page_id: pageId,
            component_id: '*', // Access to all components on the page
            access_level: 'admin',
            granted_by: 'admin'
          });
        }
      }

      // Add specific component access (only if not already exists)
      // selectedComponents now contains "page_id:component_id" strings
      for (const componentKey of selectedComponents) {
        const [pageId, componentId] = componentKey.split(':');
        
        const exists = currentDashboardAccess.some((access: any) => 
          access.page_id === pageId && access.component_id === componentId
        );
        if (!exists) {
          await userApi.addDashboardAccess(selectedUser.id, {
            user_email: selectedUser.user_email,
            org_id: selectedUser.org_id,
            page_id: pageId,
            component_id: componentId,
            access_level: 'admin',
            granted_by: 'admin'
          });
        }
      }

      // Remove unchecked dashboard page access (pages with component_id = '*')
      const currentPageIds = currentDashboardAccess
        .filter((access: any) => access.component_id === '*')
        .map((access: any) => access.page_id);
      const pagesToRemove = currentPageIds.filter(pageId => !selectedPages.includes(pageId));
      
      for (const pageId of pagesToRemove) {
        await userApi.removeDashboardAccess(selectedUser.id, pageId, '*');
      }

      // Remove unchecked component access
      const currentComponentKeys = currentDashboardAccess
        .filter((access: any) => access.component_id !== '*')
        .map((access: any) => `${access.page_id}:${access.component_id}`);
      const componentsToRemove = currentComponentKeys.filter(key => !selectedComponents.includes(key));
      
      for (const componentKey of componentsToRemove) {
        const [pageId, componentId] = componentKey.split(':');
        await userApi.removeDashboardAccess(selectedUser.id, pageId, componentId);
      }

      setShowUserPermissionsModal(false);
      loadUsers();
    } catch (error) {
      console.error('Error updating user permissions:', error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);

  // Deleting the last row of the last page can leave us past the end.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <p className="mt-2 text-gray-600">
            Manage users and their organization assignments
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Add User
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search users by username, email, or organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                        {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => {
                    const org = organizations.find(o => o.org_id === user.org_id);
                    return (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.cognito_username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.user_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {org?.name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openEditModal(user)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => openStorePermissionsModal(user)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Store Permissions
                            </button>
                            <button
                              onClick={() => openUserPermissionsModal(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Permissions
                            </button>
                            <button
                              onClick={() => handleDisable(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Disable
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center space-x-2 text-sm text-gray-700">
          <label htmlFor="pageSize">Rows per page</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="border border-gray-300 rounded-md py-1 pl-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {[25, 50, 100].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>

        <div className="text-sm text-gray-700">
          {total === 0 ? 'No users' : `Showing ${firstRow}-${lastRow} of ${total}`}
        </div>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create User</h3>
              {createError && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {createError}
                </div>
              )}
              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cognito_username}
                    onChange={(e) => setFormData({ ...formData, cognito_username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cognito User ID
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cognito_username}
                    onChange={(e) => setFormData({ ...formData, cognito_username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  {userInfo?.is_admin ? (
                    <select
                      required
                      value={formData.org_id}
                      onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Organization</option>
                      {organizations.map((org) => (
                        <option key={org.org_id} value={org.org_id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={userInfo?.org_name || formData.org_id}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    />
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit User</h3>
              <form onSubmit={handleUpdate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cognito_username}
                    onChange={(e) => setFormData({ ...formData, cognito_username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Store Permissions Modal */}
      {showStorePermissionsModal && selectedUser && (
        <UserStorePermissionsModal
          user={selectedUser}
          stores={stores}
          onSave={handleStorePermissionsUpdate}
          onClose={() => setShowStorePermissionsModal(false)}
        />
      )}

      {/* User Permissions Modal */}
      {showUserPermissionsModal && selectedUser && (
        <UserPermissionsModal
          user={selectedUser}
          stores={stores}
          dashboardPages={dashboardPages}
          onSave={handleUserPermissionsUpdate}
          onClose={() => setShowUserPermissionsModal(false)}
        />
      )}
    </div>
  );
};

// User Store Permissions Modal Component
interface UserStorePermissionsModalProps {
  user: User;
  stores: Store[];
  onSave: (selectedStores: string[]) => void;
  onClose: () => void;
}

const UserStorePermissionsModal: React.FC<UserStorePermissionsModalProps> = ({
  user,
  stores,
  onSave,
  onClose
}) => {
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    // Load current store access for the user
    const loadCurrentAccess = async () => {
      try {
        const currentAccess = await userApi.getStoreAccess(user.id);
        const currentStoreIds = currentAccess.map((access: any) => access.store_id);
        setSelectedStores(currentStoreIds);
      } catch (error) {
        console.error('Error loading current store access:', error);
      }
    };
    loadCurrentAccess();
  }, [user.id]);

  // Filter stores based on search term
  const filteredStores = stores.filter(store => 
    store.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.org_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle select all functionality
  const handleSelectAll = () => {
    const allFilteredStoreIds = filteredStores.map(store => store.store_id);
    const allSelected = allFilteredStoreIds.every(id => selectedStores.includes(id));
    
    if (allSelected) {
      // Deselect all filtered stores
      setSelectedStores(selectedStores.filter(id => !allFilteredStoreIds.includes(id)));
    } else {
      // Select all filtered stores
      setSelectedStores([...new Set([...selectedStores, ...allFilteredStoreIds])]);
    }
  };

  // Check if all filtered stores are selected
  const allFilteredSelected = filteredStores.length > 0 && 
    filteredStores.every(store => selectedStores.includes(store.store_id));

  const handleSave = () => {
    onSave(selectedStores);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Store Permissions for {user.cognito_username}
          </h3>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">
              Select the stores this user can access:
            </p>
            
            {/* Search Box */}
            <div className="mb-3">
              <input
                type="text"
                placeholder="Search stores by name or organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            {/* Select All Checkbox */}
            {filteredStores.length > 0 && (
              <div className="mb-3 pb-2 border-b border-gray-200">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-800">
                    Select All ({filteredStores.length} stores)
                  </span>
                </label>
              </div>
            )}

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredStores.length === 0 ? (
                <div className="text-sm text-gray-500 italic text-center py-4">
                  {searchTerm ? 'No stores found matching your search.' : 'No stores available.'}
                </div>
              ) : (
                filteredStores.map((store) => (
                  <label key={store.store_id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedStores.includes(store.store_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStores([...selectedStores, store.store_id]);
                        } else {
                          setSelectedStores(selectedStores.filter(s => s !== store.store_id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {store.store_name} ({store.org_id})
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Save Permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// User Permissions Modal Component
interface UserPermissionsModalProps {
  user: User;
  stores: Store[];
  dashboardPages: PageWithComponents[];
  onSave: (selectedStores: string[], selectedPages: string[], selectedComponents: string[]) => void;
  onClose: () => void;
}

const UserPermissionsModal: React.FC<UserPermissionsModalProps> = ({
  user,
  stores,
  dashboardPages,
  onSave,
  onClose
}) => {
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [storeSearchTerm, setStoreSearchTerm] = useState<string>('');
  const [pageSearchTerm, setPageSearchTerm] = useState<string>('');
  const [componentSearchTerm, setComponentSearchTerm] = useState<string>('');

  useEffect(() => {
    // Load current permissions
    const loadPermissions = async () => {
      try {
        // Load current store access
        const storeAccess = await userApi.getStoreAccess(user.id);
        setSelectedStores(storeAccess.map((access: any) => access.store_id));

        // Load current dashboard access
        const dashboardAccess = await userApi.getDashboardAccess(user.id);
        const pageIds = Array.from(new Set(dashboardAccess.map((access: any) => access.page_id)));
        setSelectedPages(pageIds);

        const componentKeys = dashboardAccess
          .filter((access: any) => access.component_id !== '*')
          .map((access: any) => `${access.page_id}:${access.component_id}`);
        setSelectedComponents(componentKeys);
      } catch (error) {
        console.error('Error loading permissions:', error);
        // Initialize with empty arrays if no permissions exist
        setSelectedStores([]);
        setSelectedPages([]);
        setSelectedComponents([]);
      }
    };
    loadPermissions();
  }, [user.id, dashboardPages]);

  // Filter stores based on search term
  const filteredStores = stores.filter(store => 
    store.store_name.toLowerCase().includes(storeSearchTerm.toLowerCase()) ||
    store.org_id.toLowerCase().includes(storeSearchTerm.toLowerCase())
  );

  // Filter pages based on search term
  const filteredPages = dashboardPages.filter(page => 
    page.page_name.toLowerCase().includes(pageSearchTerm.toLowerCase())
  );

  // Filter components based on search term
  const filteredComponents = dashboardPages.filter(page => 
    page.page_name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
    page.components.some(component => 
      component.component_name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
      (component.component_type && component.component_type.toLowerCase().includes(componentSearchTerm.toLowerCase()))
    )
  ).map(page => ({
    ...page,
    components: componentSearchTerm ? 
      page.components.filter(component => 
        component.component_name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
        (component.component_type && component.component_type.toLowerCase().includes(componentSearchTerm.toLowerCase())) ||
        page.page_name.toLowerCase().includes(componentSearchTerm.toLowerCase())
      ) : page.components
  }));

  // Handle select all functionality for stores
  const handleSelectAllStores = () => {
    const allFilteredStoreIds = filteredStores.map(store => store.store_id);
    const allSelected = allFilteredStoreIds.every(id => selectedStores.includes(id));
    
    if (allSelected) {
      setSelectedStores(selectedStores.filter(id => !allFilteredStoreIds.includes(id)));
    } else {
      setSelectedStores([...new Set([...selectedStores, ...allFilteredStoreIds])]);
    }
  };

  // Handle select all functionality for pages
  const handleSelectAllPages = () => {
    const allFilteredPageIds = filteredPages.map(page => page.page_id);
    const allSelected = allFilteredPageIds.every(id => selectedPages.includes(id));
    
    if (allSelected) {
      setSelectedPages(selectedPages.filter(id => !allFilteredPageIds.includes(id)));
    } else {
      setSelectedPages([...new Set([...selectedPages, ...allFilteredPageIds])]);
    }
  };

  // Check if all filtered stores are selected
  const allFilteredStoresSelected = filteredStores.length > 0 && 
    filteredStores.every(store => selectedStores.includes(store.store_id));

  // Check if all filtered pages are selected
  const allFilteredPagesSelected = filteredPages.length > 0 && 
    filteredPages.every(page => selectedPages.includes(page.page_id));

  const handleSave = () => {
    onSave(selectedStores, selectedPages, selectedComponents);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Manage Permissions - {user.cognito_username}
          </h3>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Stores */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Stores</h4>
              
              {/* Search Box */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search stores by name or organization..."
                  value={storeSearchTerm}
                  onChange={(e) => setStoreSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Select All Checkbox */}
              {filteredStores.length > 0 && (
                <div className="mb-3 pb-2 border-b border-gray-200">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={allFilteredStoresSelected}
                      onChange={handleSelectAllStores}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-800">
                      Select All ({filteredStores.length} stores)
                    </span>
                  </label>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredStores.length === 0 ? (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    {storeSearchTerm ? 'No stores found matching your search.' : 'No stores available.'}
                  </div>
                ) : (
                  filteredStores.map((store) => (
                    <label key={store.store_id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedStores.includes(store.store_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStores([...selectedStores, store.store_id]);
                          } else {
                            setSelectedStores(selectedStores.filter(s => s !== store.store_id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {store.store_name} ({store.org_id})
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Dashboard Pages */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Dashboard Pages</h4>
              
              {/* Search Box */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search pages by name..."
                  value={pageSearchTerm}
                  onChange={(e) => setPageSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Select All Checkbox */}
              {filteredPages.length > 0 && (
                <div className="mb-3 pb-2 border-b border-gray-200">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={allFilteredPagesSelected}
                      onChange={handleSelectAllPages}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-800">
                      Select All ({filteredPages.length} pages)
                    </span>
                  </label>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredPages.length === 0 ? (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    {pageSearchTerm ? 'No pages found matching your search.' : 'No pages available.'}
                  </div>
                ) : (
                  filteredPages.map((page) => (
                    <label key={page.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPages.includes(page.page_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPages([...selectedPages, page.page_id]);
                          } else {
                            setSelectedPages(selectedPages.filter(p => p !== page.page_id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {page.page_name} ({page.components.length} components)
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Components */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Components</h4>
              
              {/* Search Box */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search components by name, type, or page..."
                  value={componentSearchTerm}
                  onChange={(e) => setComponentSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredComponents.length === 0 ? (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    {componentSearchTerm ? 'No components found matching your search.' : 'No dashboard pages available. Create pages and components first.'}
                  </div>
                ) : (
                  filteredComponents.map((page) => {
                    const pageComponentKeys = page.components.map(c => `${page.page_id}:${c.component_id}`);
                    const allPageComponentsSelected = pageComponentKeys.length > 0 && 
                      pageComponentKeys.every(key => selectedComponents.includes(key));
                    const somePageComponentsSelected = pageComponentKeys.some(key => selectedComponents.includes(key));
                    
                    return (
                      <div key={page.id} className="border rounded p-3 bg-gray-50">
                        <div className="flex items-center mb-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={allPageComponentsSelected}
                              ref={(input) => {
                                if (input) input.indeterminate = somePageComponentsSelected && !allPageComponentsSelected;
                              }}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Select all components in this page
                                  const newComponents = Array.from(new Set([...selectedComponents, ...pageComponentKeys]));
                                  setSelectedComponents(newComponents);
                                } else {
                                  // Deselect all components in this page
                                  setSelectedComponents(selectedComponents.filter(c => !pageComponentKeys.includes(c)));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-800">
                              {page.page_name} ({page.components.length} components)
                            </span>
                          </label>
                        </div>
                        {page.components.length === 0 ? (
                          <div className="text-xs text-gray-500 italic ml-6">
                            No components in this page
                          </div>
                        ) : (
                          <div className="ml-6 space-y-1">
                            {page.components.map((component) => {
                              const componentKey = `${page.page_id}:${component.component_id}`;
                              return (
                                <label key={component.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedComponents.includes(componentKey)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedComponents([...selectedComponents, componentKey]);
                                      } else {
                                        setSelectedComponents(selectedComponents.filter(c => c !== componentKey));
                                      }
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="ml-2 text-xs text-gray-700">
                                    {component.component_name} {component.component_type && `(${component.component_type})`}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Save Permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
